// @mui
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import { Container, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
// hooks
import useResponsive from '../../hooks/useResponsive';

// ----------------------------------------------------------------------

const CORE_VALUES = [
  {
    year: '2000',
    title: 'Oppstart',
    description:
      'BEA etablert. 2 ansatte med kontor og lager i 20m\u00B2 leid kjellerlokale. Omsetning: 1,5 millioner.',
  },
  {
    year: '2008',
    title: 'BEA Offshore blir etablert',
    description:
      'BEA er vokst til 17 ansatte og omsetter for 20 millioner. BEA Offshore ble etablert som eget selskap ved CCB på Ågotnes.',
  },
  {
    year: '2018',
    title: 'Nye lokaler',
    description:
      'På grunn av økt vekst flytter vi nå inn i nytt bygg på Nedre Nøttveit 62. Her samles BEA og BEA Offshore med lager, verksted og kontor. Selskapet har nå 40 ansatte og omsetter for 47 millioner.',
  },
  {
    year: '2022',
    title: 'Satser på grønn innovasjon',
    description:
      'Budsjettert omsetning på 80 millioner og 45 ansatte. BEA er etablert som miljøfyrtårn og bytter bilpark fra fossildrevet til elektrisk. Gjennom vår satsning på ladeløsninger, solcelleinstallasjoner og styresystemer ønsker vi å være med å forme morgendagens installasjoner.',
  },
];

const COLORS = ['primary', 'secondary', 'warning', 'success'] as const;

const RootStyle = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.neutral,
  padding: theme.spacing(10, 0),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(15, 0),
  },
}));

// ----------------------------------------------------------------------

export default function AboutStory() {
  const isDesktop = useResponsive('up', 'md');

  return (
    <RootStyle>
      <Container>
        <Stack
          spacing={3}
          sx={{
            maxWidth: 480,
            textAlign: 'center',
            mx: 'auto',
            mb: { xs: 8, md: 15 },
          }}
        >
          <Typography variant="h2">Vår historie</Typography>

          <Typography sx={{ color: 'text.secondary' }}>
            Vi har gjennom 20 års drift utdannet 33 lærlinger til bestått fagbrev som elektriker og
            har i dag 11 lærlinger under utdanning.
          </Typography>
        </Stack>

        <Timeline position={isDesktop ? 'alternate' : 'right'}>
          {CORE_VALUES.map((value, index) => {
            const { title, description, year } = value;
            return (
              <TimelineItem
                key={title}
                sx={{
                  '&:before': {
                    display: { xs: 'none', md: 'block' },
                  },
                }}
              >
                <TimelineSeparator>
                  <TimelineDot color={COLORS[index]} />
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ pb: { xs: 3, md: 5 } }}>
                  <Typography variant="subtitle3" sx={{ color: `${COLORS[index]}.main` }}>
                    {year}
                  </Typography>
                  <Typography variant="h4" sx={{ mt: 0.5, mb: 1 }}>
                    {title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      maxWidth: { md: 360 },
                      ...(index % 2 && {
                        ml: 'auto',
                      }),
                    }}
                  >
                    {description}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      </Container>
    </RootStyle>
  );
}
